import React, { useState } from 'react'
import { Typography, Button, Box } from '@mui/material'
import Banner from 'components/Banner'
import { useHealthStatusListener } from 'services/context/healthStatusListener'
import { useAppSelector } from 'reduxStore/store'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

/**
 * HealthStatusBanner component
 * Displays a banner with health status information from Firebase Realtime Database.
 * Only renders when there is a title in health_status_check/{establishment_id}.
 *
 * @returns {JSX.Element | null} The rendered banner or null if no title exists.
 */
const HealthStatusBanner: React.FC = () => {
  const { title } = useHealthStatusListener()
  const establishmentId = useAppSelector(
    state => state.establishment.data?.establishment_id
  )
  const [isClosed, setIsClosed] = useState(false)

  // Don't render if there's no title or if banner was closed
  if (!title || isClosed) {
    return null
  }

  const handleClose = () => {
    setIsClosed(true)
  }

  const statusUrl = `https://status.swissanalytica.ch/status/${establishmentId}`

  return (
    <Banner
      variant='filled'
      onClose={handleClose}
      sx={{
        backgroundColor: 'rgba(255, 193, 7, 0.8)',
        zIndex: 1300,
        borderRadius: 0,
        padding: '10px 24px',
        paddingLeft: '85px',
        alignItems: 'center',
        width: '100%',
        '& .MuiAlert-message': {
          width: '100%',
          padding: 0
        },
        '& .MuiAlert-action': {
          paddingTop: 0,
          '& .MuiIconButton-root': {
            color: 'white'
          }
        }
      }}
    >
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
        gap={2}
      >
        <Typography variant='body1' fontWeight='medium' sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Button
          variant='text'
          href={statusUrl}
          target='_blank'
          rel='noopener noreferrer'
          endIcon={<OpenInNewIcon />}
          sx={{
            color: 'white',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Ver detalhes
        </Button>
      </Box>
    </Banner>
  )
}

export default HealthStatusBanner
